'use client';
import { useState, useEffect, useRef } from 'react';
import { useShipping, formatDaysLabel } from './useShipping';
import { PanelContent, BottomSheet } from './ShippingEstimate';
import { OverlayPanel } from 'primereact/overlaypanel';
import { DeliveryFeedbackTrigger } from './DeliveryFeedback';

// Shorter badge label for the product card — drops "business" to save space.
const formatDaysLabelCompact = (days) =>
    days != null ? `${days} Day${days !== 1 ? 's' : ''}` : '5–6 Days';

export const DeliveryInfo = ({ mini = false }) => {
    const [showTip,   setShowTip]   = useState(false);
    const [hovered,   setHovered]   = useState(false);
    const [showSheet, setShowSheet] = useState(false);
    const [isCompact, setIsCompact] = useState(false);
    const opRef = useRef(null);

    const { pincode, setPincode, result: info, loading, error, setError, checkPincode, clear, useCurrentLocation, detectingLocation } = useShipping();

    useEffect(() => {
        const update = () => setIsCompact(window.innerWidth < 1024);
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    if (mini) {
        const hasInfo = info?.serviceable;
        const sharedProps = { result: info, pincode, setPincode, loading, error, setError, checkPincode, clear, useCurrentLocation, detectingLocation };

        return (
            <>
                <button
                    onClick={(e) => isCompact ? setShowSheet(true) : opRef.current?.toggle(e)}
                    style={{
                        flex: 1, display: 'flex', flexDirection: 'column', position: 'relative',
                        alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '6px 4px',
                        background: '#f8fafc', border: '1px solid #e2e8f0',
                        borderRadius: '8px', cursor: 'pointer', minWidth: 0,
                    }}
                    onMouseEnter={() => { if (!isCompact) { setHovered(true); if (hasInfo) setShowTip(true); } }}
                    onMouseLeave={() => { setHovered(false); setShowTip(false); }}
                >
                    <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#334155', lineHeight: 1.2, textAlign: 'center' }}>
                        Delivery
                    </span>
                    {hasInfo ? (
                        <span style={{
                            fontSize: '0.6rem', fontWeight: 700, color: '#fff', background: '#14b8a6',
                            borderRadius: '999px', padding: '1.5px 7px', lineHeight: 1.3, textAlign: 'center', whiteSpace: 'nowrap',
                        }}>
                            in {formatDaysLabelCompact(info.estimatedDays)}
                        </span>
                    ) : (
                        <i className='pi pi-truck' style={{ fontSize: '0.8rem', color: '#14b8a6' }} />
                    )}

                    {/* Desktop hover tooltip */}
                    {showTip && hasInfo && (() => {
                        const days  = info.estimatedDays;
                        const verb  = days != null ? 'Delivered in' : 'Delivered within';
                        const line1 = `${verb} ${formatDaysLabel(days)} via ${info.courier}`;
                        const line2 = info.deliveryDate ? `Expected by ${info.deliveryDate}` : null;
                        return (
                            <div className="hover-tooltip" style={{
                                position: 'absolute', bottom: 'calc(100% + 8px)', left: 0,
                                background: 'var(--primary-color)', border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: 8, padding: '8px 12px', zIndex: 50,
                                boxShadow: '0 6px 20px rgba(11,49,99,0.35)', pointerEvents: 'none', minWidth: 'max-content',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <i className="pi pi-truck" style={{ fontSize: '0.7rem', color: 'var(--secondary-color)' }} />
                                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#ffffff' }}>{line1}</span>
                                </div>
                                {line2 && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                                        <i className="pi pi-calendar" style={{ fontSize: '0.7rem', color: 'var(--secondary-color)' }} />
                                        <span style={{ fontSize: '0.72rem', fontWeight: 500, color: 'rgba(255,255,255,0.8)' }}>{line2}</span>
                                    </div>
                                )}
                                <div style={{
                                    position: 'absolute', top: '100%', left: 14,
                                    width: 8, height: 8, background: 'var(--primary-color)',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    borderTop: 'none', borderLeft: 'none',
                                    transform: 'rotate(45deg)', marginTop: -4,
                                }} />
                            </div>
                        );
                    })()}
                </button>

                {!isCompact && (
                    <OverlayPanel
                        ref={opRef}
                        className="p-0"
                        style={{
                            width: '360px', maxWidth: '95vw',
                            borderRadius: '1rem', overflow: 'hidden',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
                            border: '1px solid var(--surface-border)',
                        }}
                    >
                        <PanelContent onClose={() => opRef.current?.hide()} source="product_card" {...sharedProps} />
                    </OverlayPanel>
                )}

                {showSheet && (
                    <BottomSheet onClose={() => setShowSheet(false)} source="product_card" {...sharedProps} />
                )}
            </>
        );
    }

    // Full mode — shows inline delivery estimate strip with feedback trigger
    if (!info?.serviceable) return null;

    const days  = info.estimatedDays;
    const verb  = days != null ? 'Delivered in' : 'Delivered within';
    const line1 = `${verb} ${formatDaysLabel(days)} via ${info.courier}`;
    const line2 = info.deliveryDate ? `Expected by ${info.deliveryDate}` : null;
    const label = line2 ? `${line1} · ${line2}` : line1;

    return (
        <div
            className="flex align-items-center gap-1 mt-1 mb-1 px-2 py-1 border-1 border-100 border-round-lg"
            style={{ background: 'var(--surface-50)', minWidth: 0, position: 'relative', cursor: 'pointer' }}
            onMouseEnter={() => { setHovered(true); setShowTip(true); }}
            onMouseLeave={() => { setHovered(false); setShowTip(false); }}
        >
            <i className="pi pi-truck text-teal-500" style={{ fontSize: '0.72rem', flexShrink: 0 }} />
            <span style={{
                fontSize: '0.72rem', fontWeight: 700,
                overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                minWidth: 0, flex: 1, transition: 'color 0.15s',
                textDecoration: hovered ? 'underline' : 'none',
            }}>
                {label}
            </span>
            <DeliveryFeedbackTrigger
                pincode={info.pincode}
                source="product_card_inline"
                variant="inline"
            />

            {showTip && (
                <div style={{
                    position: 'absolute', bottom: 'calc(100% + 8px)', left: 0,
                    background: 'var(--primary-color)', border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 8, padding: '8px 12px', zIndex: 50,
                    boxShadow: '0 6px 20px rgba(11,49,99,0.35)', pointerEvents: 'none', minWidth: 'max-content',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className="pi pi-truck" style={{ fontSize: '0.7rem', color: 'var(--secondary-color)' }} />
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#ffffff' }}>{line1}</span>
                    </div>
                    {line2 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                            <i className="pi pi-calendar" style={{ fontSize: '0.7rem', color: 'var(--secondary-color)' }} />
                            <span style={{ fontSize: '0.72rem', fontWeight: 500, color: 'rgba(255,255,255,0.8)' }}>{line2}</span>
                        </div>
                    )}
                    <div style={{
                        position: 'absolute', top: '100%', left: 14,
                        width: 8, height: 8, background: 'var(--primary-color)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderTop: 'none', borderLeft: 'none',
                        transform: 'rotate(45deg)', marginTop: -4,
                    }} />
                </div>
            )}
        </div>
    );
};
